import cloudListStateMixin from '@/mixins/cloudListStateMixin'
import {
  getCloudAppState,
  normalizeCloudSortField,
  setCloudAppState
} from '@/utils/cloudState'
import {
  getMobileCloudViewMode,
  MOBILE_CLOUD_VIEW_MODE_EVENT,
  normalizeMobileCloudViewMode,
  setMobileCloudViewMode
} from '@/utils/mobileCloudViewState'

const COMPONENT_APP_KEYS = {
  PaperSync: 'paper',
  MeetingSync: 'meeting',
  MemoSync: 'memo',
  LearningSync: 'learning',
  KnowledgeBaseSync: 'knowledge',
  DailySync: 'daily'
}

const baseMethods = cloudListStateMixin.methods
const cloudListHelpers = {
  normalizePaperIdentityText: baseMethods.normalizePaperIdentityText,
  normalizePaperIdentityKey: baseMethods.normalizePaperIdentityKey,
  getPaperFolderRawValue: baseMethods.getPaperFolderRawValue,
  isPaperSentinelResourceId: baseMethods.isPaperSentinelResourceId,
  getPaperFolderPathKey: baseMethods.getPaperFolderPathKey,
  normalizePaperFolderPathItem: baseMethods.normalizePaperFolderPathItem,
  getPaperItemKey: baseMethods.getPaperItemKey,
  normalizeCloudPaperFolderPath: baseMethods.normalizeCloudPaperFolderPath,
  shouldRestoreCloudListAppState: baseMethods.shouldRestoreCloudListAppState
}

export default {
  data() {
    return {
      cloudListStateRestoring: false,
      mobileCloudListReturnScrollTop: null,
      mobileCloudListReturnScrollToken: 0
    }
  },
  created() {
    this.restoreCloudListState()
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener(MOBILE_CLOUD_VIEW_MODE_EVENT, this.handleMobileCloudViewModeChange)
    }
    this.$nextTick(() => {
      this.applyMobileCloudViewMode(getMobileCloudViewMode(this.viewMode))
      this.$nextTick(() => {
        this.cloudListStateRestoring = false
      })
    })
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(MOBILE_CLOUD_VIEW_MODE_EVENT, this.handleMobileCloudViewModeChange)
    }
  },
  watch: {
    detailMode(value, oldValue) {
      this.handleMobileCloudListPreviewStateChange(value, oldValue)
    },
    dailyPreviewOpen(value, oldValue) {
      this.handleMobileCloudListPreviewStateChange(value, oldValue)
    },
    paperPasswordModalOpen(value, oldValue) {
      if (!value && oldValue) {
        this.$nextTick(() => {
          if (!this.paperPasswordModalOpen && !this.isMobileCloudListPreviewOpen()) {
            this.clearMobileCloudListReturnScroll()
          }
        })
        return
      }
      if (!value || oldValue || this.mobileCloudListReturnScrollTop !== null) {
        return
      }
      if (this.paperPasswordAction == 'download') {
        return
      }
      this.captureMobileCloudListReturnScroll()
    },
    viewMode(value) {
      if (!this.cloudListStateRestoring) {
        const viewMode = setMobileCloudViewMode(value)
        if (this.isDailyMode && !this.isDailyTodoTab && 'dailyViewMode' in this && this.dailyViewMode != viewMode) {
          this.dailyViewMode = viewMode
        }
      }
      this.saveCloudListState()
    },
    paperListResourceId() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    paperFolderPath: {
      deep: true,
      handler() {
        this.clearMobileCloudListReturnScroll()
        this.saveCloudListState()
      }
    },
    paperFilter() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    sortField() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    sortDesc() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    searchOpen() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    searchKeyword() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    dailyActiveTab(value, oldValue) {
      if (value !== oldValue) {
        this.clearMobileCloudListReturnScroll()
      }
      if (!this.cloudListStateRestoring && this.isDailyMode && 'dailyViewMode' in this) {
        const dailyViewMode = value == 'todo' ? 'list' : getMobileCloudViewMode(this.viewMode)
        if (this.dailyViewMode != dailyViewMode) {
          this.dailyViewMode = dailyViewMode
        }
      }
      if (!this.cloudListStateRestoring && this.isDailyMode && value !== oldValue) {
        this.scrollMobileCloudListToTop()
      }
      this.saveCloudListState()
    },
    dailyPeriod() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    dailyTodoFinishFilter() {
      this.clearMobileCloudListReturnScroll()
      this.saveCloudListState()
    },
    dailyViewMode(value) {
      if (!this.cloudListStateRestoring && this.isDailyMode && !this.isDailyTodoTab) {
        const viewMode = setMobileCloudViewMode(value, true)
        if ('viewMode' in this && this.viewMode != viewMode) {
          this.viewMode = viewMode
        }
      }
      this.saveCloudListState()
    }
  },
  methods: Object.assign({}, cloudListHelpers, {
    clearMobileCloudListReturnScroll() {
      this.mobileCloudListReturnScrollTop = null
      this.mobileCloudListReturnScrollToken += 1
    },
    getMobileCloudListScrollArea() {
      const target = typeof this.getMobilePullRefreshScrollTarget == 'function'
        ? this.getMobilePullRefreshScrollTarget()
        : (this.$refs.mobilePullRefreshArea || this.$refs.paperListArea)
      return Array.isArray(target) ? target[0] : target
    },
    isMobileCloudListPreviewOpen() {
      return !!(
        (typeof this.detailMode == 'boolean' && this.detailMode)
        || (typeof this.dailyPreviewOpen == 'boolean' && this.dailyPreviewOpen)
      )
    },
    handleMobileCloudListPreviewStateChange(value, oldValue) {
      if (value === oldValue) {
        return
      }
      if (value) {
        this.captureMobileCloudListReturnScroll()
        return
      }
      if (oldValue) {
        this.restoreMobileCloudListReturnScroll()
      }
    },
    captureMobileCloudListReturnScroll() {
      if (this.mobileCloudListReturnScrollTop !== null) {
        return
      }
      const area = this.getMobileCloudListScrollArea()
      if (!area) {
        return
      }
      this.mobileCloudListReturnScrollToken += 1
      this.mobileCloudListReturnScrollTop = Math.max(0, Number(area.scrollTop) || 0)
    },
    restoreMobileCloudListReturnScroll() {
      if (this.mobileCloudListReturnScrollTop === null) {
        return
      }
      const scrollTop = this.mobileCloudListReturnScrollTop
      const token = this.mobileCloudListReturnScrollToken
      this.mobileCloudListReturnScrollTop = null
      const applyScroll = () => {
        if (
          this._isBeingDestroyed
          || this._isDestroyed
          || token != this.mobileCloudListReturnScrollToken
          || this.isMobileCloudListPreviewOpen()
        ) {
          return
        }
        const area = this.getMobileCloudListScrollArea()
        if (area) {
          area.scrollTop = scrollTop
        }
      }
      this.$nextTick(() => {
        applyScroll()
        if (typeof window != 'undefined' && typeof window.requestAnimationFrame == 'function') {
          window.requestAnimationFrame(() => {
            applyScroll()
            window.requestAnimationFrame(applyScroll)
          })
        }
      })
    },
    scrollMobileCloudListToTop() {
      this.clearMobileCloudListReturnScroll()
      this.$nextTick(() => {
        const target = typeof this.getMobilePullRefreshScrollTarget == 'function'
          ? this.getMobilePullRefreshScrollTarget()
          : (this.$refs.mobilePullRefreshArea || this.$refs.paperListArea)
        const area = Array.isArray(target) ? target[0] : target
        if (area) {
          area.scrollTop = 0
        }
      })
    },
    getCloudListStateAppKey() {
      const rawKey = this.$options.cloudStateKey || COMPONENT_APP_KEYS[this.$options.name] || ''
      const appKey = String(rawKey || '').replace(/^wap-/, '')
      return appKey ? `wap-${appKey}` : ''
    },
    restoreCloudListState() {
      this.cloudListStateRestoring = true
      const appKey = this.getCloudListStateAppKey()
      const persistedState = getCloudAppState(appKey) || {}
      const shouldRestoreAppState = this.shouldRestoreCloudListAppState()
      const shouldRestoreDailyFilters = shouldRestoreAppState || this.$options.restoreDailyFiltersWithoutPageState === true
      const state = shouldRestoreAppState ? persistedState : {}
      const sharedViewMode = getMobileCloudViewMode(this.viewMode)
      this.applyMobileCloudViewMode(sharedViewMode)
      if ('sortField' in this) {
        this.sortField = normalizeCloudSortField(persistedState.sortField || this.sortField)
      }
      if ('sortDesc' in this && typeof persistedState.sortDesc == 'boolean') {
        this.sortDesc = persistedState.sortDesc
      }
      this.restoreMobileCloudPageState(state, persistedState, shouldRestoreDailyFilters, sharedViewMode)
    },
    restoreMobileCloudPageState(state, persistedState, shouldRestoreDailyFilters, sharedViewMode) {
      if ('paperListResourceId' in this && typeof state.paperListResourceId == 'string') {
        this.paperListResourceId = state.paperListResourceId
      }
      if ('paperFolderPath' in this && Array.isArray(state.paperFolderPath)) {
        this.paperFolderPath = this.normalizeCloudPaperFolderPath(state.paperFolderPath)
      }
      if ('paperFilter' in this && ['all', 'pdf', 'favorite', 'deleted'].includes(state.paperFilter)) {
        this.paperFilter = state.paperFilter
      }
      if ('searchOpen' in this && typeof state.searchOpen == 'boolean') {
        this.searchOpen = state.searchOpen
      }
      if ('searchKeyword' in this && typeof state.searchKeyword == 'string') {
        this.searchKeyword = state.searchKeyword
      }
      if ('dailyActiveTab' in this && ['todo', 'tasks', 'notes'].includes(state.dailyActiveTab)) {
        this.dailyActiveTab = state.dailyActiveTab
      }
      this.restoreMobileCloudDailyState(persistedState, shouldRestoreDailyFilters, sharedViewMode)
    },
    restoreMobileCloudDailyState(state, shouldRestoreDailyFilters, sharedViewMode) {
      if (shouldRestoreDailyFilters && 'dailyPeriod' in this && ['month', 'week', 'day'].includes(state.dailyPeriod)) {
        this.dailyPeriod = state.dailyPeriod
      }
      if (shouldRestoreDailyFilters && 'dailyTodoFinishFilter' in this && [-1, 0, 1].includes(Number(state.dailyTodoFinishFilter))) {
        this.dailyTodoFinishFilter = Number(state.dailyTodoFinishFilter)
      }
      if ('dailyViewMode' in this) {
        this.dailyViewMode = this.isMobileCloudDailyTodoView()
          ? 'list'
          : (this.isDailyMode ? normalizeMobileCloudViewMode(sharedViewMode) : this.dailyViewMode)
      }
    },
    saveCloudListState() {
      if (this.cloudListStateRestoring) {
        return
      }
      const appKey = this.getCloudListStateAppKey()
      if (!appKey) {
        return
      }
      setCloudAppState(appKey, this.getMobileCloudListState())
    },
    getMobileCloudListState() {
      return {
        sortField: normalizeCloudSortField(this.sortField),
        sortDesc: this.sortDesc === true,
        paperListResourceId: this.paperListResourceId || '',
        paperFolderPath: this.normalizeCloudPaperFolderPath(this.paperFolderPath),
        paperFilter: this.paperFilter || 'all',
        searchOpen: !!this.searchOpen,
        searchKeyword: this.searchKeyword || '',
        dailyActiveTab: this.dailyActiveTab || '',
        dailyPeriod: this.dailyPeriod || '',
        dailyTodoFinishFilter: this.dailyTodoFinishFilter
      }
    },
    isMobileCloudDailyTodoView() {
      return !!(this.isDailyMode && this.isDailyTodoTab)
    },
    applyMobileCloudViewMode(mode) {
      const viewMode = normalizeMobileCloudViewMode(mode)
      if ('viewMode' in this && this.viewMode != viewMode) {
        this.viewMode = viewMode
      }
      if (this.isDailyMode && 'dailyViewMode' in this) {
        const dailyViewMode = this.isDailyTodoTab ? 'list' : viewMode
        if (this.dailyViewMode != dailyViewMode) {
          this.dailyViewMode = dailyViewMode
        }
      }
      return viewMode
    },
    handleMobileCloudViewModeChange(event) {
      this.applyMobileCloudViewMode(event && event.detail ? event.detail.viewMode : '')
    }
  })
}
